/**
 * AP-18 — Assistent: Anfragen und Typen.
 *
 * Liegt in `lib/` und nicht im Feature `ai`, weil die Seitenleiste im Rahmen der Anwendung hängt
 * (`AppLayout`) und Seiten anderer Features ihr sagen, was geöffnet ist — ein Import aus dem
 * Feature wäre ein Querimport (Modulgrenzen AP-21-B).
 */
import type { AssistentBereich } from '@symphenia/shared-types'
import { api } from '@/lib/axios'
import { mitStand } from '@/lib/speichern/stand-header'
import type { AssistentBeilage } from './assistent-beilagen'

/** Wo der Benutzer ist (Spezifikation 6.) — Auskunft für den Assistenten, keine Erlaubnis. */
export interface AssistentKontext {
  bereich?: AssistentBereich
  seite?: string
  seitenTitel?: string
  art?: string
  id?: string
  bezeichnung?: string
  auswahl?: string[]
}

export type AssistentSperre = 'AI_DISABLED' | 'OFF' | 'NO_KEY' | 'NO_QUOTA' | 'QUOTA_EXHAUSTED' | 'DAILY_LIMIT'

export interface AssistentKontingent {
  limit: number
  used: number
  warn: boolean
  exhausted: boolean
}

export interface AssistentStand {
  ready: boolean
  blocked: AssistentSperre | null
  keySource: 'TENANT' | 'CENTRAL'
  quota: AssistentKontingent | null
  /** die Tagesgrenze des Benutzers, wenn der Betrieb eine gesetzt hat (12.2) */
  daily?: AssistentKontingent | null
  canConfigure: boolean
}

export interface AssistentSchritt {
  tool: string
  title: string
  state: 'RUNNING' | 'DONE' | 'FAILED'
}

/** Warum eine Zeile nicht übernommen wurde — in der Form einer API-Fehlerantwort (`apiErrorText` übersetzt sie). */
export interface AssistentVorschlagFehler {
  code?: string
  message: string
  permission?: string
}

export interface AssistentVorschlagZeile {
  id: string
  /** in Alltagssprache: „Urlaub · Pascal Huber · Fr 3.10.2026 · 8 Std." */
  text: string
  status: 'OPEN' | 'APPLIED' | 'FAILED' | 'REJECTED'
  /** Verweis auf das Angelegte (`art:ziel`, wie in einer Antwort) */
  link?: string
  note?: string
  error?: AssistentVorschlagFehler
}

/** Ein Vorschlag des Assistenten (7.4): geändert wird erst mit „Übernehmen". */
export interface AssistentVorschlag {
  id: string
  tool: string
  title: string
  status: 'OPEN' | 'APPLYING' | 'APPLIED' | 'PARTIAL' | 'REJECTED' | 'EXPIRED'
  /** Es gibt noch Zeilen, die sich übernehmen lassen */
  open: boolean
  expiresAt: string
  appliedAt: string | null
  items: AssistentVorschlagZeile[]
}

export interface AssistentNachricht {
  id: string
  role: 'USER' | 'ASSISTANT'
  text: string
  status: 'RUNNING' | 'DONE' | 'FAILED' | 'STOPPED'
  error?: string | null
  steps?: AssistentSchritt[]
  proposals?: AssistentVorschlag[]
  /** was ein Fachmodul unter die Antwort gelegt hat — gezeichnet von ihm (`assistent-beilagen.ts`) */
  attachments?: AssistentBeilage[]
  atLimit?: boolean
  tokens?: number
  createdAt: string
}

export interface GespraechKopf {
  id: string
  /** leer = noch ohne Titel */
  title: string
  pinned: boolean
  context: { area: string | null; kind: string | null; id: string | null; label: string | null } | null
  lastMessageAt: string
  createdAt: string
}

export interface Gespraech extends GespraechKopf {
  running: boolean
  messages: AssistentNachricht[]
}

export interface AssistentWerkzeugInfo {
  name: string
  title: string
  description: string
  kind: 'LESEN' | 'VORSCHLAG' | 'ANSTOSSEN'
  area: string
  front: boolean
}

export interface AssistentEinstellungen {
  enabled: boolean
  keySource: 'TENANT' | 'CENTRAL'
  consentCurrent: boolean
  consentAt: string | null
  consentText: string
  consentTextVersion: string
  tenantKey: boolean
  aiEnabled: boolean
  quota: AssistentKontingent
  /** Tagesgrenze je Benutzer in Tokens; `null` = keine (12.2) */
  dailyTokenLimitPerUser: number | null
  /** Gespräche laufen so viele Tage nach der letzten Nachricht ab */
  retentionDays: number
  /** die Bereiche, die der Mandant sperren kann — nur zu gebuchten Bausteinen (AS-17) */
  areas: { key: string; excluded: boolean }[]
  limits: { dailyMin: number; dailyMax: number; retentionMin: number; retentionMax: number }
  updatedAt: string
}

/** Was sich an den Einstellungen ändern lässt — feldweise, `null` leert die Tagesgrenze. */
export interface AssistentEinstellungenAenderung {
  enabled?: boolean
  keySource?: 'TENANT' | 'CENTRAL'
  consent?: boolean
  dailyTokenLimitPerUser?: number | null
  retentionDays?: number
  /** die ganze Liste der gesperrten Bereiche */
  excludedAreas?: string[]
}

export interface AssistentVerbrauchZeile {
  userId: string
  name: string
  inputTokens: number
  outputTokens: number
  totalTokens: number
  /** beantwortete Fragen */
  requests: number
  /** nur im laufenden Monat gefüllt */
  todayTokens: number
}

/** Verbrauch des Assistenten in einem Monat je Benutzer — Zahlen, nie Inhalte (12.2). */
export interface AssistentVerbrauch {
  period: string
  current: boolean
  users: AssistentVerbrauchZeile[]
  total: { inputTokens: number; outputTokens: number; totalTokens: number; requests: number }
}

export interface GespraechListe {
  data: GespraechKopf[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}

const B = '/ai/assistant'

export const assistentApi = {
  stand: () => api.get<{ data: AssistentStand }>(`${B}/status`).then((r) => r.data.data),
  /** Bis zu drei Beispielfragen für das Eingabefeld eines Dashboards (5.3); ohne Bereich die des Hauptdashboards. */
  beispiele: (bereich?: string) => api.get<{ data: string[] }>(`${B}/examples`, { params: bereich ? { context: bereich } : {} }).then((r) => r.data.data),
  /** Die Werkzeuge des Benutzers; `art` = Art des geöffneten Datensatzes, dessen gebundene Werkzeuge dazukommen. */
  werkzeuge: (bereich?: string, art?: string) => api.get<{ data: AssistentWerkzeugInfo[] }>(`${B}/tools`, { params: { ...(bereich ? { context: bereich } : {}), ...(art ? { kind: art } : {}) } }).then((r) => r.data.data),
  /** Eigene Gespräche; mit `contextKind` und `contextId` nur die, die bei diesem Datensatz begannen. */
  liste: (params: { q?: string; page?: number; limit?: number; contextKind?: string; contextId?: string } = {}) => api.get<GespraechListe>(`${B}/conversations`, { params }).then((r) => r.data),
  lesen: (id: string) => api.get<{ data: Gespraech }>(`${B}/conversations/${id}`).then((r) => r.data.data),
  /** Neues Gespräch mit der ersten Frage — Titel und Ort kommen daraus. */
  beginnen: (text: string, context?: AssistentKontext) => api.post<{ data: Gespraech }>(`${B}/conversations`, { text, ...(context ? { context } : {}) }).then((r) => r.data.data),
  fragen: (id: string, text: string, context?: AssistentKontext) =>
    api.post<{ data: Gespraech }>(`${B}/conversations/${id}/messages`, { text, ...(context ? { context } : {}) }).then((r) => r.data.data),
  stoppen: (id: string) => api.post<{ data: Gespraech }>(`${B}/conversations/${id}/stop`).then((r) => r.data.data),
  aendern: (id: string, dto: { title?: string; pinned?: boolean }) => api.patch<{ data: Gespraech }>(`${B}/conversations/${id}`, dto).then((r) => r.data.data),
  loeschen: (id: string) => api.delete(`${B}/conversations/${id}`).then(() => undefined),
  /** Übernimmt die gewählten Zeilen eines Vorschlags; ohne Auswahl alle offenen. */
  vorschlagUebernehmen: (id: string, itemIds?: string[]) => api.post<{ data: AssistentVorschlag }>(`${B}/proposals/${id}/apply`, itemIds ? { itemIds } : {}).then((r) => r.data.data),
  vorschlagVerwerfen: (id: string) => api.post<{ data: AssistentVorschlag }>(`${B}/proposals/${id}/reject`, {}).then((r) => r.data.data),
  einstellungen: () => api.get<{ data: AssistentEinstellungen }>(`${B}/settings`).then((r) => r.data.data),
  /** `stand` (AP-36): der Stand, auf dem die Maske arbeitet — eine ältere Fassung wird abgewiesen. */
  einstellungenAendern: (dto: AssistentEinstellungenAenderung, stand?: string) =>
    api.patch<{ data: AssistentEinstellungen }>(`${B}/settings`, dto, mitStand(stand)).then((r) => r.data.data),
  /** Verbrauch je Benutzer; `period` als `JJJJ-MM`, ohne Angabe der laufende Monat (`ai.config.read`). */
  verbrauch: (period?: string) => api.get<{ data: AssistentVerbrauch }>(`${B}/usage`, { params: period ? { period } : {} }).then((r) => r.data.data),
}
